import { BaseFeature } from '../../core/base-feature.js';
import { SiegeGameEngine } from './game-engine.js';
import { generateWave, getWaveConfig } from './wave-generator.js';
import { EffectsManager, getPowerups, getDebuffs } from './effects.js';

// Overlay imports - these will be dynamically imported when needed
// to avoid cross-folder import issues with Vite
let overlayRegistry = null;
let SiegeOverlay = null;

/**
 * SiegeFeature - Frontend feature for the Siege base-defense game
 *
 * Responsibilities:
 * - Manage game state and lifecycle
 * - Receive cursor position/clicks from desktop
 * - Run game engine and wave generation
 * - Trigger lockout when base is captured
 */
export class SiegeFeature extends BaseFeature {
    constructor(coreAPI) {
        super(coreAPI);

        // Overlay registered flag
        this.overlayRegistered = false;

        // Game engine
        this.engine = null;

        // Effects manager
        this.effectsManager = null;

        // Game configuration
        this.gameConfig = {
            difficulty: 'normal',
            lockoutMinutes: 5,
            waveDelay: 3000,      // Delay between waves (ms)
            autoStartWaves: true  // Automatically start next wave
        };

        // State
        this.isGameActive = false;
        this.currentWave = 0;
        this.waveTimer = null;

        // Update callbacks for React components
        this.updateCallbacks = new Set();

        // Pending promises
        this._pendingRequests = new Map();

        // Throttle desktop state updates (~30fps instead of 60fps)
        this.lastDesktopUpdate = 0;
        this.desktopUpdateInterval = 33; // ms between updates
    }

    static getMetadata() {
        return {
            id: 'siege',
            name: 'siege',
            version: 'v1.0',
            capabilities: [
                'start',
                'stop',
                'pause',
                'resume',
                'get_state',
                'trigger_lockout'
            ],
            dependencies: []
        };
    }

    async initialize() {
        console.log('[Siege] Initializing...');

        try {
            // Create game engine
            this.engine = new SiegeGameEngine({
                onStateChange: (state) => this.handleEngineStateChange(state),
                onUnitDestroyed: (unit) => this.handleUnitDestroyed(unit),
                onBaseHit: (damage, health) => this.handleBaseHit(damage, health),
                onGameOver: (reason) => this.handleGameOver(reason),
                onWaveComplete: () => this.handleWaveComplete()
            });

            // Create effects manager
            this.effectsManager = new EffectsManager(this.engine);

            // Register overlay (dynamic import to avoid cross-folder issues)
            if (!this.overlayRegistered) {
                try {
                    const overlayModule = await import('../../src/components/overlays/OverlayRegistry.js');
                    const overlayComponent = await import('../../src/components/overlays/SiegeOverlay.jsx');

                    overlayRegistry = overlayModule.overlayRegistry;
                    SiegeOverlay = overlayComponent.default;

                    if (overlayRegistry && SiegeOverlay) {
                        overlayRegistry.register('siege', SiegeOverlay, {
                            priority: 175,
                            name: 'Siege Game Overlay',
                            enabled: true
                        });
                        this.overlayRegistered = true;
                        console.log('[Siege] Overlay registered');
                    }
                } catch (overlayError) {
                    console.warn('[Siege] Could not register overlay (non-fatal):', overlayError.message);
                }
            }

            console.log('[Siege] Initialized successfully');
        } catch (error) {
            console.error('[Siege] Failed to initialize:', error);
            throw error;
        }
    }

    async handleMessage(message) {
        const { action, payload } = message;

        switch (action) {
            // Messages from desktop
            case 'cursor_update':
                return this.handleCursorUpdate(payload);

            case 'cursor_click':
                return this.handleCursorClick(payload);

            case 'game_started':
                // Confirmation from desktop that game started
                console.log('[Siege] Desktop confirmed game started');
                return;

            case 'game_ended':
                // Confirmation from desktop that game ended
                console.log('[Siege] Desktop confirmed game ended:', payload?.reason);
                return;

            // Response messages
            case 'start_response':
                return this.handleStartResponse(payload);

            case 'stop_response':
            case 'end_response':
                return this.handleStopResponse(payload);

            case 'get_state_response':
                return this.handleGetStateResponse(payload);

            default:
                console.warn(`[Siege] Unknown action: ${action}`);
        }
    }

    // =====================================
    // Game Control
    // =====================================

    /**
     * Start a new game
     * @param {Object} config - Game configuration
     */
    async startGame(config = {}) {
        if (this.isGameActive) {
            return { success: false, error: 'Game already active' };
        }

        console.log('[Siege] Starting game with config:', config);

        // Merge config
        this.gameConfig = { ...this.gameConfig, ...config };

        // Reset engine with new config
        this.engine.updateConfig({
            difficulty: this.gameConfig.difficulty,
            baseHealth: this.gameConfig.baseHealth || 100
        });
        this.engine.reset();

        // Tell desktop to start game and create overlay
        this.sendMessage('start', {
            difficulty: this.gameConfig.difficulty,
            lockoutMinutes: this.gameConfig.lockoutMinutes,
            baseHealth: this.gameConfig.baseHealth || 100
        });

        // Start engine
        this.engine.start();
        this.isGameActive = true;
        this.currentWave = 0;

        // Start first wave after short delay
        setTimeout(() => {
            if (this.isGameActive) {
                this.startNextWave();
            }
        }, 1000);

        this.notifyUpdateListeners();
        return { success: true };
    }

    /**
     * Stop the game
     * @param {string} reason - Why the game is stopping
     */
    async stopGame(reason = 'manual') {
        if (!this.isGameActive) {
            return { success: false, error: 'No game active' };
        }

        console.log(`[Siege] Stopping game: ${reason}`);

        // Clear wave timer
        if (this.waveTimer) {
            clearTimeout(this.waveTimer);
            this.waveTimer = null;
        }

        // Stop engine
        this.engine.stop();
        this.isGameActive = false;

        // Tell desktop to stop cursor tracking
        this.sendMessage('stop', { reason });

        this.notifyUpdateListeners();
        return { success: true, reason };
    }

    /**
     * Pause the game
     */
    pauseGame() {
        if (!this.isGameActive) return;
        this.engine.pause();
        this.notifyUpdateListeners();
    }

    /**
     * Resume the game
     */
    resumeGame() {
        if (!this.isGameActive) return;
        this.engine.resume();
        this.notifyUpdateListeners();
    }

    /**
     * Start the next wave
     */
    startNextWave() {
        if (!this.isGameActive) return;

        this.currentWave++;
        const waveConfig = getWaveConfig(this.currentWave, this.gameConfig.difficulty);
        const units = generateWave(waveConfig);

        console.log(`[Siege] Starting wave ${this.currentWave}:`, units);

        this.engine.startWave(this.currentWave, units);
        this.notifyUpdateListeners();
    }

    // =====================================
    // Input Handling
    // =====================================

    /**
     * Handle cursor position update from desktop
     */
    handleCursorUpdate(payload) {
        if (!this.isGameActive) return;
        this.engine.updateCursor(payload.x, payload.y);
    }

    /**
     * Handle cursor click from desktop
     */
    handleCursorClick(payload) {
        if (!this.isGameActive) return;
        this.engine.handleClick(payload.x, payload.y);
    }

    // =====================================
    // Game Events
    // =====================================

    /**
     * Handle engine state change
     */
    handleEngineStateChange(state) {
        // Check if wave is complete
        if (this.isGameActive && !state.isPaused && this.engine.isWaveComplete()) {
            this.handleWaveComplete();
        }

        this.notifyUpdateListeners();
    }

    /**
     * Handle unit destroyed
     */
    handleUnitDestroyed(unit) {
        // Notify desktop for explosion effects on overlay
        this.sendMessage('unit_destroyed', {
            x: unit.x,
            y: unit.y,
            color: unit.color,
            type: unit.type
        });

        // Could add sound effects, particle effects, etc.
        this.emit('unit_destroyed', unit);
    }

    /**
     * Handle base being damaged
     */
    handleBaseHit(damage, health) {
        this.emit('base_hit', { damage, health });
    }

    /**
     * Handle wave completion
     */
    handleWaveComplete() {
        if (!this.isGameActive || this.waveTimer) return;

        console.log(`[Siege] Wave ${this.currentWave} complete!`);
        this.emit('wave_complete', { wave: this.currentWave });

        // Start next wave after delay (if auto-start enabled)
        if (this.gameConfig.autoStartWaves) {
            this.waveTimer = setTimeout(() => {
                this.waveTimer = null;
                if (this.isGameActive) {
                    this.startNextWave();
                }
            }, this.gameConfig.waveDelay);
        }
    }

    /**
     * Handle game over
     */
    async handleGameOver(reason) {
        console.log(`[Siege] Game over: ${reason}`);

        // Clear wave timer
        if (this.waveTimer) {
            clearTimeout(this.waveTimer);
            this.waveTimer = null;
        }

        // Tear the game down like the manual Stop button: stop the engine and tell
        // the desktop to stop cursor tracking / close the overlay. Previously
        // handleGameOver only flipped isGameActive, so on a natural game-over the
        // desktop overlay froze and lockouts stacked on top of a live game.
        this.engine?.stop();
        this.isGameActive = false;
        this.sendMessage('stop', { reason });

        // If base was captured, trigger lockout
        if (reason === 'base_captured') {
            await this.triggerLockout();
        }

        this.emit('game_over', {
            reason,
            score: this.engine.score,
            wave: this.currentWave,
            stats: this.engine.stats
        });

        this.notifyUpdateListeners();
    }

    /**
     * Trigger desktop lockout
     */
    async triggerLockout() {
        console.log(`[Siege] Triggering lockout for ${this.gameConfig.lockoutMinutes} minutes`);

        this.sendMessage('trigger_lockout', {
            reason: 'base_captured',
            lockoutMinutes: this.gameConfig.lockoutMinutes
        });
    }

    // =====================================
    // Response Handlers
    // =====================================

    handleStartResponse(payload) {
        const resolve = this._pendingRequests.get('start');
        if (resolve) {
            this._pendingRequests.delete('start');
            resolve(payload);
        }
    }

    handleStopResponse(payload) {
        const resolve = this._pendingRequests.get('stop');
        if (resolve) {
            this._pendingRequests.delete('stop');
            resolve(payload);
        }
    }

    handleGetStateResponse(payload) {
        const resolve = this._pendingRequests.get('get_state');
        if (resolve) {
            this._pendingRequests.delete('get_state');
            resolve(payload);
        }
    }

    // =====================================
    // Event Listeners
    // =====================================

    /**
     * Subscribe to update events
     * @param {Function} callback - Called when state changes
     * @returns {Function} Unsubscribe function
     */
    onUpdate(callback) {
        this.updateCallbacks.add(callback);
        return () => this.updateCallbacks.delete(callback);
    }

    notifyUpdateListeners() {
        const state = this.getGameState();

        // Send state to desktop for overlay rendering (throttled to ~30fps)
        if (this.isGameActive) {
            const now = Date.now();
            if (now - this.lastDesktopUpdate >= this.desktopUpdateInterval) {
                this.lastDesktopUpdate = now;
                this.sendMessage('state_update', state);
            }
        }

        for (const callback of this.updateCallbacks) {
            try {
                callback(state);
            } catch (error) {
                console.error('[Siege] Error in update callback:', error);
            }
        }
    }

    // =====================================
    // State Getters
    // =====================================

    /**
     * Get full game state for rendering
     */
    getGameState() {
        const engineState = this.engine?.getState() || {};

        return {
            isGameActive: this.isGameActive,
            currentWave: this.currentWave,
            gameConfig: { ...this.gameConfig },
            activeEffects: this.effectsManager?.getActiveEffects() || [],
            ...engineState
        };
    }

    /**
     * Check if game is active
     */
    getIsGameActive() {
        return this.isGameActive;
    }

    /**
     * Get current score
     */
    getScore() {
        return this.engine?.score || 0;
    }

    /**
     * Get current wave
     */
    getCurrentWave() {
        return this.currentWave;
    }

    // =====================================
    // Effects Control
    // =====================================

    /**
     * Apply an effect (powerup or debuff)
     * @param {string} effectId - Effect ID to apply
     * @returns {boolean} Success
     */
    applyEffect(effectId) {
        if (!this.isGameActive || !this.effectsManager) {
            console.warn('[Siege] Cannot apply effect: game not active');
            return false;
        }

        return this.effectsManager.applyEffect(effectId);
    }

    /**
     * Get available powerups
     * @returns {Object[]}
     */
    getAvailablePowerups() {
        return getPowerups();
    }

    /**
     * Get available debuffs
     * @returns {Object[]}
     */
    getAvailableDebuffs() {
        return getDebuffs();
    }

    /**
     * Get active effects
     * @returns {Object[]}
     */
    getActiveEffects() {
        return this.effectsManager?.getActiveEffects() || [];
    }

    // =====================================
    // Cleanup
    // =====================================

    async cleanup() {
        console.log('[Siege] Cleaning up...');

        if (this.isGameActive) {
            await this.stopGame('cleanup');
        }

        if (this.waveTimer) {
            clearTimeout(this.waveTimer);
            this.waveTimer = null;
        }

        // Unregister overlay
        if (this.overlayRegistered && overlayRegistry) {
            try {
                overlayRegistry.unregister('siege');
            } catch (e) {
                console.warn('[Siege] Error unregistering overlay:', e);
            }
            this.overlayRegistered = false;
        }

        // Clear effects
        if (this.effectsManager) {
            this.effectsManager.clearAll();
            this.effectsManager = null;
        }

        this.engine = null;
        this.updateCallbacks.clear();
        this._pendingRequests.clear();
    }
}
